/**
 * Code-aware error resolver for the ToS offline-acceptance endpoints
 * (ADR-0017 A6). Each rule the API enforces has its own code, so the
 * operator reads the rule that fired. A 401/403 (missing
 * `tos.offline.manage`) gets a fixed copy and never echoes the response
 * body. Side-effect free — unit-tested in `offline-error.test.ts`.
 */
import { errorCodeOf } from "@/shared/api/error-code";
import { ApiError } from "@/shared/api/http-client";

type Translate = (key: string, values?: Record<string, unknown>) => string;

const ERROR_KEY_BY_CODE: Readonly<Record<string, string>> = {
  OFFLINE_ACCEPTANCE_REFERENCE_REQUIRED: "users.tos.errors.referenceRequired",
  OFFLINE_ACCEPTANCE_USER_NOT_FOUND: "users.tos.errors.userNotFound",
  TOS_DOCUMENT_TYPE_NOT_FOUND: "users.tos.errors.documentTypeNotFound",
  OFFLINE_ACCEPTANCE_NOT_FOUND: "users.tos.errors.notFound",
  OFFLINE_ACCEPTANCE_ALREADY_EXISTS: "users.tos.errors.alreadyExists",
  OFFLINE_ACCEPTANCE_ALREADY_REVOKED: "users.tos.errors.alreadyRevoked",
};

const FORBIDDEN_KEY = "users.tos.errors.forbidden";
const GENERIC_KEY = "users.tos.errors.generic";

export function resolveOfflineAcceptanceError(err: unknown, t: Translate): string {
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
    return t(FORBIDDEN_KEY);
  }
  const code = errorCodeOf(err);
  const key = code ? ERROR_KEY_BY_CODE[code] : undefined;
  return t(key ?? GENERIC_KEY);
}
